import type { Role } from "./roles";

/**
 * Sharing for works and media (docs/plans/sharing.md). Each item has an owner, a general access
 * (`restricted`: only the people added; `team`: everyone, with contributors at `teamLevel` and editors
 * able to edit), and people added at a level. Admins always have full access. The API enforces this;
 * the Studio uses the same rules to show controls.
 */
export const ACCESS_LEVELS = ["view", "comment", "edit"] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];
export type Visibility = "restricted" | "team";
/** What someone may do with an item: a level, or `owner` (everything, including sharing it). */
export type Access = AccessLevel | "owner";

const RANK: Record<Access, number> = { view: 1, comment: 2, edit: 3, owner: 4 };
const best = (a: Access | null, b: Access | null): Access | null => (!a ? b : !b ? a : RANK[a] >= RANK[b] ? a : b);

export type Shareable = { ownerId: string | null; visibility: Visibility; teamLevel: AccessLevel };

/** Someone's access to an item, from their role, the item's general access, and their own row (if added). */
export function accessFor(user: { id: string; role: Role }, item: Shareable, added: AccessLevel | null): Access | null {
  if (user.role === "admin" || (item.ownerId !== null && item.ownerId === user.id)) return "owner";
  const team: Access | null = item.visibility === "team" ? (user.role === "editor" ? "edit" : item.teamLevel) : null;
  return best(team, added);
}

/** Whether `access` allows at least `need`. */
export const allows = (access: Access | null, need: Access) => access !== null && RANK[access] >= RANK[need];

/** Publishing, sending back and archiving: an editor or admin with edit access. */
export const canPublishItem = (role: Role, access: Access | null) => (role === "admin" || role === "editor") && allows(access, "edit");

export const ACCESS_LABELS: Record<Access, string> = { view: "Can view", comment: "Can comment", edit: "Can edit", owner: "Owner" };
